// useBilling Hook
// Custom hook for fetching and managing billing information

import { useState, useEffect, useCallback } from 'react';
import { billingAPI, type BillingInfo } from '../api/billing';

import { logger } from '../utils/logger';

interface UseBillingReturn {
  billing: BillingInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  creditPercentage: number;
  isLowCredits: boolean;
  isOutOfCredits: boolean;
}

/**
 * Custom hook to fetch and manage billing information
 * @param organizationId - Organization ID to fetch billing for
 * @param autoRefresh - Whether to auto-refresh data (default: false)
 * @param refreshInterval - Refresh interval in milliseconds (default: 5 minutes)
 * @returns Billing data, loading state, error, and refetch function
 */
export function useBilling(
  organizationId: string | undefined,
  autoRefresh: boolean = false,
  refreshInterval: number = 5 * 60 * 1000 // 5 minutes
): UseBillingReturn {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate credit percentage
  const creditPercentage = billing
    ? (billing.plan.limits.credits.used / billing.plan.limits.credits.limit) * 100
    : 0;

  // Check if credits are low (< 20%)
  const isLowCredits = billing
    ? billing.plan.limits.credits.remaining < billing.plan.limits.credits.limit * 0.2
    : false;

  // Check if out of credits
  const isOutOfCredits = billing
    ? billing.plan.limits.credits.remaining <= 0
    : false;

  // Fetch billing data and subscription info
  const fetchBilling = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      logger.log('🔵 Fetching billing info for org:', organizationId);

      // Fetch billing info - it now includes everything (plan, billing_period, subscription)
      const billingData = await billingAPI.getBillingInfo(organizationId);

      logger.log('✅ Billing info fetched:', billingData);
      setBilling(billingData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch billing information';
      logger.error('❌ Error fetching billing:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Initial fetch
  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!autoRefresh || !organizationId) return;

    const intervalId = setInterval(() => {
      logger.log('🔄 Auto-refreshing billing data...');
      fetchBilling();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, organizationId, refreshInterval, fetchBilling]);

  return {
    billing,
    loading,
    error,
    refetch: fetchBilling,
    creditPercentage,
    isLowCredits,
    isOutOfCredits,
  };
}

